import React, { useEffect, useRef, useState } from "react"
import { pdfjs } from "react-pdf"
import { PDFDocument } from "pdf-lib"
import { blobToURL } from "../../utils/Utils"
import { computeSignaturePlacement } from "../../utils/signaturePlacement"
import { AddSigDialog } from "../../components/esign/AddSigDialog"
import { SignIntentDialog } from "../../components/esign/SignIntentDialog"
import ViewPdf from "../../components/ViewPdf/ViewPdf"
import { setTemplate, signMainDoc, getLsaTypeDoc } from "../../features/documents/documents"
import { useDispatch, useSelector } from "react-redux"
import Spinner from "../../components/Spiner/Spiner"
import Button from "../../components/Button/Button"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { getBaseUserByIdAnonymous } from "../../features/user/user"
import { getBankAccountAnonymous } from "../../features/payment/paymentSlice"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { useNavigate, useSearchParams } from "react-router-dom"
import usePinnedFeatureFlag from "../../hooks/usePinnedFeatureFlag"
import { ESIGN_LSA_SIGN_INTENT, LSA_SIGNED_COPY_DOWNLOAD, SIGNING_THANK_YOU_TEXT } from "../../config/featureFlagKeys"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { keepSignedCopy, forgetSignedCopy } from "../../utils/signedCopy"
import { ESIGN_CONSENT, esignConsentText } from "../../config/esignConsent"
import { getLinkToken, LINK_TOKEN_REFUSAL } from "../../utils/linkToken"
import { LSA_NOT_READY_CODES } from "../../constants/lsaChargeName"
import { signingSuccessText } from "./signingSuccessText"

const LSA_TYPE_BY_TYPE_DOC = {
  legalServiceAgreement: 'english',
  legalServiceAgreementSpanish: 'bilingual',
}

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.js`

const SignContract = ({ userId, isEnrollment, onLinkRefused }) => {
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 640) // 640px - sm breakpoint in Tailwind

  const extractSignatureCoordinates = async (pdfFile) => {
    const loadingTask = pdfjs.getDocument(pdfFile)
    const pdfDoc = await loadingTask.promise
    const totalPages = pdfDoc.numPages
    const signatureCoordinates = []

    for (let i = 1; i <= totalPages; i++) {
      const page = await pdfDoc.getPage(i)
      const textContent = await page.getTextContent()


      textContent.items.forEach((item) => {
        if (item.str.includes("{account_signature}")) {
          signatureCoordinates.push({
            page: i,
            x: item.transform[4], y: item.transform[5], width: item.width
          })
        }
      })
    }
    setInit(false)
    setCoordinates(signatureCoordinates)
    return signatureCoordinates
  }

  const dispatch = useDispatch()

  const styles = {
    documentBlock: {
      maxWidth: isLargeScreen ? 612 : 400,
      margin: "0 auto",
    },
  }

  const { triggerLoading } = useMyContextLoading()
  const navigate = useNavigate()

  const [paymentBankMethod, setPaymentBankMethod] = useState(null)
  const [user, setUser] = useState('')
  const [lsaTypeDoc, setLsaTypeDoc] = useState(null)
  const [searchParams] = useSearchParams()
  const lsaQueryParam = searchParams.get('lsa')

  const [pdf, setPdf] = useState('')
  // WS #2228903: the plan version the server rendered this agreement from, sent back with the signature.
  const [agreementVersion, setAgreementVersion] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [refusalMessage, setRefusalMessage] = useState(null)

  const [signatureURL, setSignatureURL] = useState(false)

  const [disableSignature, setDisableSignature] = useState(false)

  const [signatureDialogVisible, setSignatureDialogVisible] = useState(false)
  const [pageNum, setPageNum] = useState(0)
  const [resetPage, setResetPage] = useState(null)
  const [totalPages, setTotalPages] = useState(0)

  const [coordinates, setCoordinates] = useState([])


  const [pageDetails, setPageDetails] = useState(null)
  const documentRef = useRef(null)

  const [init, setInit] = useState(true)

  const { enabled: intentFlag, pin: pinIntentFlag } = usePinnedFeatureFlag(ESIGN_LSA_SIGN_INTENT)
  const [serverRequiresIntent, setServerRequiresIntent] = useState(false)
  const intentMode = intentFlag || serverRequiresIntent
  const SignatureDialog = intentMode ? SignIntentDialog : AddSigDialog
  const [intent, setIntent] = useState(null)
  const unsignedPdfRef = useRef(null)
  // WS #2219705: the success page offers the agreement just signed as a download. A copy from an earlier
  // signing in this tab is dropped first, so an already-signed link never shows one.
  const signedCopyDownload = useSelector(selectFeatureFlag(LSA_SIGNED_COPY_DOWNLOAD))
  useEffect(() => { forgetSignedCopy() }, [])

  const thankYouTextFlag = useSelector(selectFeatureFlag(SIGNING_THANK_YOU_TEXT))

  // const bounds = {left: 0, top: 0, right: 558, bottom: 738}


  const getUser = async () => {
    const response = await dispatch(getBaseUserByIdAnonymous(userId))
    setUser(response?.payload?.data)
  }
  const fetchBankData = async () => {
    const maxAttempts = 3
    const retryDelayMs = 1500
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const action = await dispatch(getBankAccountAnonymous({ userId }))
      if (getBankAccountAnonymous.fulfilled.match(action)) {
        const data = action.payload?.data
        if (data) {
          setPaymentBankMethod(data)
        } else {
          setLoadError(true)
        }
        return
      }
      if (action?.payload?.code === LINK_TOKEN_REFUSAL) {
        onLinkRefused?.(action.payload.message)
        return
      }
      if (attempt < maxAttempts) await wait(retryDelayMs)
    }

    setLoadError(true)
    showSnackBar("error", "Unable to load your payment information. Please try again later or contact support.")
  }
  const getTemplateDoc = async (typeDoc) => {
    try {
      setLoadError(false)
      setRefusalMessage(null)
      const response = await dispatch(setTemplate({
        typeDoc: typeDoc || "legalServiceAgreement",
        useEstimatedPayments: true,
        data: {
          billingAccountId: paymentBankMethod?._id,
          userId
        }
      }))

      const data = setTemplate.fulfilled.match(response) ? response.payload?.data : null

      if (response?.payload?.code === LINK_TOKEN_REFUSAL) {
        onLinkRefused?.(response.payload.message)
        return
      }

      if (LSA_NOT_READY_CODES.includes(response?.payload?.code)) {
        setRefusalMessage(response.payload.message || null)
        setLoadError(true)
        return
      }

      if (!data) {
        setLoadError(true)
        showSnackBar("error", "Unable to load the document. Please try again later or contact support.")
        return
      }

      setAgreementVersion(response.payload?.agreementVersion || null)
      setPdf(data)
    } catch (error) {
      setLoadError(true)
      showSnackBar("error", "Unable to load the document. Please try again later or contact support.")
    }
  }

  const resolveLsaTypeDoc = async () => {
    const response = await dispatch(getLsaTypeDoc({ userId, lsa: lsaQueryParam }))
    if (getLsaTypeDoc.fulfilled.match(response)) {
      const resolved = response.payload?.data?.typeDoc
      if (resolved) {
        setLsaTypeDoc(resolved)
        return resolved
      }
    }
    setLsaTypeDoc('legalServiceAgreement')
    return 'legalServiceAgreement'
  }

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([getUser(), fetchBankData(), resolveLsaTypeDoc()])
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (user && paymentBankMethod && lsaTypeDoc) {
      getTemplateDoc(lsaTypeDoc)
    }
  }, [user, paymentBankMethod, lsaTypeDoc])

  const goToSuccess = () => navigate("/success/signing", {
    replace: true,
    state: {
      text: isEnrollment
        ? { heading: "Congratulations!", subtext: "In the next 10 seconds you'll receive a call." }
        : signingSuccessText(thankYouTextFlag),
      ...(user?.firmBrand ? { firmBrand: user.firmBrand } : {}),
    }
  })

  const onSigningAuthRequired = () => {
    setServerRequiresIntent(true)
    if (unsignedPdfRef.current) setPdf(unsignedPdfRef.current)
    setDisableSignature(false)
    setSignatureDialogVisible(true)
  }

  // WS #2228903. The plan or card split changed after this page rendered the agreement: render it
  // again and ask for the signature again, on the agreement as it now stands.
  const onAgreementChanged = (message, { accountChanged = false } = {}) => {
    showSnackBar("error", message || "This agreement was updated. Review the new version and sign again.")
    unsignedPdfRef.current = null
    setSignatureURL(false)
    setDisableSignature(false)
    setCoordinates([])
    setInit(true)
    setPdf('')
    if (accountChanged) fetchBankData()
    else getTemplateDoc(lsaTypeDoc)
  }

  const onChargeNameMissing = (message) => {
    unsignedPdfRef.current = null
    setSignatureURL(false)
    setDisableSignature(false)
    setCoordinates([])
    setInit(true)
    setPdf('')
    setRefusalMessage(message || null)
    setLoadError(true)
  }

  const intentFields = () => ({
    typedName: intent?.typedName,
    consent: intent?.consent,
    linkTokenPresented: !!getLinkToken(),
  })

  const sign = async (intentBody) => {
    triggerLoading(true)
    try {
      const response = await dispatch(signMainDoc({
        userId,
        pdf,
        creator: user?.creator?._id ? user?.creator?._id : user?.creator,
        lsaTemplateType: LSA_TYPE_BY_TYPE_DOC[lsaTypeDoc] || null,
        ...(agreementVersion ? { agreementVersion } : {}),
        ...intentBody,
      }))

      // rejected thunks resolve (no .unwrap()), so the catch below never sees API
      // failures — without this check a 4xx was a silent no-op on the public page
      if (response?.error) {
        const refusal = response.payload
        if (refusal?.code === 'alreadySigned') {
          triggerLoading(false)
          goToSuccess()
          return
        }
        if (refusal?.code === 'signingAuthRequired') {
          onSigningAuthRequired()
          triggerLoading(false)
          return
        }
        if (refusal?.code === LINK_TOKEN_REFUSAL) {
          triggerLoading(false)
          onLinkRefused?.(refusal.message)
          return
        }
        if (refusal?.code === 'agreementChanged') {
          onAgreementChanged(refusal.message)
          triggerLoading(false)
          return
        }
        if (refusal?.code === 'agreementFileChanged') {
          onAgreementChanged(refusal.message, { accountChanged: true })
          triggerLoading(false)
          return
        }
        if (LSA_NOT_READY_CODES.includes(refusal?.code)) {
          onChargeNameMissing(refusal.message)
          triggerLoading(false)
          return
        }
        const message = typeof refusal === 'string' ? refusal : refusal?.message
        showSnackBar("error", message || "An error occurred while signing the document")
        triggerLoading(false)
        return
      }

      const data = response?.payload?.data

      if (data?.filePath?.length > 0) {
        if (signedCopyDownload) keepSignedCopy(pdf)
        showSnackBar("success", "The document has been successfully sent")
        goToSuccess()
      }
    } catch (error) {
      console.error("Error occurred while signing:", error)
      showSnackBar("error", "An error occurred while signing the document")
    }
    triggerLoading(false)
  }

  const send = () => (intentMode ? sign(intentFields()) : sign())


  useEffect(() => {
    const handleExtractSignature = () => {
      if (pdf && pageDetails && init) {
        extractSignatureCoordinates(pdf)
          .then((data) => onSet(data, false))
          .catch((error) => {
            console.error("Error extracting signature coordinates:", error)
          })
      }
    }
    handleExtractSignature()
  }, [pdf, pageDetails, init])

  useEffect(() => {
    if (signatureURL) {
      onSet(coordinates)
      setDisableSignature(true)
    }
  }, [signatureURL])


  const onSet = async (coordinates, setSign = true) => {
    if (!pdf || !pageDetails) return
    if (setSign) unsignedPdfRef.current = pdf

    const { originalHeight, originalWidth } = pageDetails
    const scale =
      originalWidth / documentRef.current.clientWidth

    const pdfDoc = await PDFDocument.load(pdf)

    const pages = pdfDoc.getPages()

    coordinates?.map(async (item) => {

      setResetPage(item?.page - 1)
      setPageNum(item?.page - 1)

      let firstPage = pages[item?.page - 1]

      if (setSign) {

        const pngImage = await pdfDoc.embedPng(signatureURL)

        // Bounded to the reserved one-line signature slot so it can't cover the
        // surrounding text on the LSA (see computeSignaturePlacement).
        const placement = computeSignaturePlacement({
          markerX: item?.x,
          markerY: item?.y,
          markerWidth: item?.width,
          imageWidth: pngImage.width,
          imageHeight: pngImage.height,
          scale,
        })
        firstPage.drawImage(pngImage, placement)
      } else {
        const pngImageBg = await pdfDoc.embedPng('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGIAAAAQCAYAAADtT6rEAAAAOXRFWHRTb2Z0d2FyZQBNYXRwbG90bGliIHZlcnNpb24zLjYuMywgaHR0cHM6Ly9tYXRwbG90bGliLm9yZy/P9b71AAAACXBIWXMAAA9hAAAPYQGoP6dpAAAAQ0lEQVR4nO3RQQ0AIBDAMMC/5+OBCPZoFSzZnplZfHd+B/AYEWFEhBERRkQYEWFEhBERRkQYEWFEhBERRkQYEWFExAV7JwQc4XdgpwAAAABJRU5ErkJggg==')
        firstPage?.drawImage(pngImageBg, {
          x: item.x + item.width - 98,
          y: item.y - (16 / 2),
          width: 98,
          height: 16,
        })
        setResetPage(0)
        setPageNum(0)
      }

    })


    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
    const URL = await blobToURL(blob)

    setPdf(URL)
    setSignatureURL(null)
  }


  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 640)
    }

    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <div className="flex flex-col-reverse md:flex-row max-w-[1360px] m-auto gap-6">
      {pdf && <div className="md:w-[500px] w-full">
        <div
          className={`user_account_updateuserform flex flex-col items-start sm:gap-6 p-2 sm:p-6 rounded-2xl bg-paper border border-divider`}>
          {!disableSignature && <div className=" stack flex flex-col items-start w-full ">
            <div
              className=" h-full w-full min-h-[56px] font-sans flex items-center cursor-pointer gap-2 rounded-lg border border-divider"
              onClick={() => {
                pinIntentFlag()
                setSignatureDialogVisible(true)
              }}>
              <div className="w-8 h-8 flex items-center justify-center ml-[14px]">
                <svg width={20} height={18} viewBox="0 0 20 18" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.80864 6.72005e-05C9.61329 0.00278663 9.42671 0.0816188 9.28859 0.219794L0.470238 9.03815C0.329578 9.17878 0.250533 9.36952 0.250488 9.56843V12.7501C0.250508 12.949 0.329532 13.1397 0.47018 13.2804C0.610828 13.421 0.801582 13.5 1.00049 13.5001H4.18214C4.38105 13.5 4.57182 13.421 4.71246 13.2803L13.5308 4.46198C13.6714 4.32132 13.7504 4.13058 13.7504 3.9317C13.7504 3.73283 13.6714 3.54209 13.5308 3.40143L10.3492 0.219794C10.2783 0.148917 10.194 0.0929569 10.1011 0.0552177C10.0083 0.0174785 9.90884 -0.00127419 9.80864 6.72005e-05ZM9.81914 1.81061L11.9402 3.9317L3.87186 12.0001H1.75071V9.87897L9.81914 1.81061Z"
                    fill="black" />
                  <path
                    d="M11.2274 10.5004C11.0325 10.5064 10.8476 10.5875 10.7118 10.7272L7.26799 14.1678C7.02544 14.4101 6.85707 14.6981 6.74944 15.0034H1.01187C0.589016 14.9974 0.243566 15.3408 0.250091 15.7586C0.257591 16.1764 0.598241 16.5077 1.01187 16.502H6.74659C6.85384 16.8107 7.02312 17.1018 7.26799 17.3464C8.14024 18.2178 9.57882 18.2179 10.451 17.3464L12.5735 15.2259C12.6725 15.4572 12.8055 15.6421 13.0115 15.8479C13.8837 16.7192 15.3208 16.7192 16.1931 15.8479L17.0456 14.9961H18.9894C20.0035 15.0104 20.0035 13.4832 18.9894 13.4975H16.735C16.5361 13.4976 16.3454 13.5765 16.2048 13.717L15.1325 14.7883C14.8295 15.091 14.3751 15.091 14.072 14.7883C13.769 14.4856 13.7691 14.0314 14.072 13.7288L14.5158 13.2853C15.1932 12.5792 14.1622 11.549 13.4553 12.2258L13.0115 12.6692L9.39042 16.2869C9.08764 16.5895 8.63149 16.5896 8.32849 16.2869C8.02549 15.9842 8.02549 15.53 8.32849 15.2273L11.7723 11.7867C12.2628 11.3104 11.9112 10.4803 11.2274 10.5004Z"
                    fill="black" />
                </svg>
              </div>
              <div
                className="value h-6 text-text-primary font-sans w-full">
                Add Signature
              </div>
            </div>
          </div>}

          <div className="flex justify-end items-center gap-4 self-stretch py-6 px-0">
            {disableSignature && <Button
              hover="hover:bg-primary-main/90" size="lg" width="w-full"
              onClick={() => {
                pinIntentFlag()
                send()
              }}>Send</Button>}

          </div>
        </div>
      </div>}
      <div className="bg-surface w-full ">
        <div style={styles.container}>
          {signatureDialogVisible ? (
            <SignatureDialog
              user={user}
              consentText={esignConsentText(lsaTypeDoc)}
              consentVersion={ESIGN_CONSENT.version}
              setSignatureDialogVisible={setSignatureDialogVisible}
              setSignatureURL={setSignatureURL}
              onClose={() => setSignatureDialogVisible(false)}
              onConfirm={(result) => {
                if (intentMode) {
                  setIntent({ typedName: result.typedName, consent: result.consent })
                  setSignatureURL(result.signatureURL)
                } else {
                  setSignatureURL(result)
                }
                setSignatureDialogVisible(false)
              }}
            />
          ) : null}

          {!pdf && loadError ? (
            <div
              role="alert"
              className="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center rounded-2xl border border-divider bg-paper">
              <div className="text-text-primary font-medium">{refusalMessage || 'This document could not be loaded'}</div>
              {!refusalMessage && (
                <div className="text-text-secondary text-sm">
                  Please refresh the page and try again, or contact support if the problem persists.
                </div>
              )}
            </div>
          ) : !pdf ? (
            <Spinner />
          ) : null}

          {pdf ? (
            <div>
              <div ref={documentRef} style={styles.documentBlock}>
                {pdf &&
                  <ViewPdf
                    sePageN={setPageNum}
                    totalPages={totalPages}
                    pdf={pdf}
                    width={!isLargeScreen && 400}
                    setups={true}
                    setTotalPages={setTotalPages}
                    setPageD={setPageDetails}
                    resetPage={resetPage}
                    onLoadError={() => setLoadError(true)}
                  />
                }
              </div>

            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default SignContract
